import { useEffect, useRef } from 'react'

/**
 * Writes `--px` / `--py` (-1..1) on a container as the pointer moves over it,
 * so `.depth-layer` children can shift by their own `--depth`. That is what
 * makes a stack of forms read as space rather than a flat picture.
 *
 * Skipped for coarse pointers (no hover) and for reduced motion.
 */
export default function usePointerParallax() {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

    let frame = 0
    let target = [0, 0]

    const paint = () => {
      frame = 0
      el.style.setProperty('--px', target[0].toFixed(3))
      el.style.setProperty('--py', target[1].toFixed(3))
    }
    const onMove = (e) => {
      const r = el.getBoundingClientRect()
      target = [((e.clientX - r.left) / r.width - 0.5) * 2, ((e.clientY - r.top) / r.height - 0.5) * 2]
      if (!frame) frame = requestAnimationFrame(paint)
    }
    const onLeave = () => {
      target = [0, 0]
      if (!frame) frame = requestAnimationFrame(paint)
    }

    el.addEventListener('pointermove', onMove, { passive: true })
    el.addEventListener('pointerleave', onLeave)
    return () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', onLeave)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return ref
}
